import React, { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { useUpdateSimCard } from '@/hooks/useSimCards';
import { ISimCard, ISimUpdateRequest } from '@/types/api';
import LoadingSpinner from './LoadingSpinner';

interface SimCardEditModalProps {
  sim: ISimCard;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (updatedSim: ISimCard) => void;
}

const SimCardEditModal: React.FC<SimCardEditModalProps> = ({
  sim,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<ISimUpdateRequest>({
    iccid: sim.iccid,
    label: sim.label || '',
    imei_lock: sim.imei_lock,
    status: sim.status,
  });

  const updateMutation = useUpdateSimCard();

  // Reset form when sim changes
  useEffect(() => {
    setFormData({
      iccid: sim.iccid,
      label: sim.label || '',
      imei_lock: sim.imei_lock,
      status: sim.status,
    });
  }, [sim]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await updateMutation.mutateAsync({
        iccid: sim.iccid,
        data: formData,
      });
      
      onSuccess?.({ ...sim, ...formData });
      onClose();
    } catch (error) {
      // Error is handled by the mutation
      logger.error('Failed to update SIM:', error);
    }
  };

  const handleInputChange = (field: keyof ISimUpdateRequest, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Edit SIM Card
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-mono">
            {sim.iccid}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Label */}
            <div>
              <label htmlFor="label" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Label
              </label>
              <input
                type="text"
                id="label"
                value={formData.label}
                onChange={(e) => handleInputChange('label', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter a label for this SIM"
              />
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value as 'Enabled' | 'Disabled')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Enabled">Enabled</option>
                <option value="Disabled">Disabled</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="imei_lock"
                checked={formData.imei_lock || false}
                onChange={(e) => handleInputChange('imei_lock', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="imei_lock" className="ml-2 block text-sm text-gray-900 dark:text-white">
                Enable IMEI Lock
              </label>
            </div>

            {/* Current Values Display */}
            <div className="bg-gray-50 rounded-md p-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Values</h4>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Label:</dt>
                  <dd className="text-gray-900 dark:text-white">{sim.label || 'None'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Status:</dt>
                  <dd className={`font-medium ${sim.status === 'Enabled' ? 'text-green-600' : 'text-red-600'}`}>
                    {sim.status}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">IMEI Lock:</dt>
                  <dd className={`font-medium ${sim.imei_lock ? 'text-blue-600' : 'text-gray-600 dark:text-gray-400'}`}>
                    {sim.imei_lock ? 'Enabled' : 'Disabled'}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Error Message */}
            {updateMutation.error && (
              <div className="rounded-md bg-red-50 p-3">
                <div className="flex">
                  <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">
                      {updateMutation.error instanceof Error 
                        ? updateMutation.error.message 
                        : 'Failed to update SIM card'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={updateMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateMutation.isLoading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {updateMutation.isLoading && (
                  <LoadingSpinner size="small" className="mr-2" />
                )}
                {updateMutation.isLoading ? 'Updating...' : 'Update SIM'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SimCardEditModal;